// ─── CGT patient-journey model ────────────────────────────────────────────────
//
// A "journey" is the ordered chain of CGT forms for a single patient. Every
// patient walks the same eight stages (CGT_FORM_ORDER); each stage has a status
// derived from that patient's submissions. This module is the single source of
// truth for that derivation so the overview rows and the detail timeline never
// disagree.
//
// NOTE: When a patient is on a specific Procedure, order and predecessor flow
// come from that procedure's sequence (GET /procedures/:id/journey), not from the
// hard-coded CGT_FORM_ORDER below — see the procedure page / ProcedureFormSidebar. The helpers here
// remain the fallback for the default cross-procedure overview where no procedure
// is assigned (patient↔procedure assignment is a later phase).

import {
  CGT_FORM_ORDER,
  CGT_FORM_LABELS,
  CGT_DEFAULT_PREDECESSORS,
} from '@/types/cgt'
import type { CgtFieldType, CgtFormType, FormSubmission, FormDefinition } from '@/types/cgt'
import { cellKey, resolveCell } from '@/lib/tableForm'
import { allFields } from '@/lib/formSections'

// `pending_review` (z8nrz7dt41) is a RUN status: a run with a form completed
// and waiting for its reviews. The procedure lists show it; a single journey
// step waiting for review still reads `in_progress`.
export type StepStatus = 'completed' | 'skipped' | 'in_progress' | 'pending_review' | 'not_started' | 'locked'

export interface JourneyStep {
  formType: CgtFormType
  label: string
  status: StepStatus
  submission?: FormSubmission
  definition?: FormDefinition
  /** Predecessors that are not yet completed (only meaningful when `locked`). */
  blockedBy: CgtFormType[]
}

/** Builds the eight-stage journey for one patient's submissions. */
export function buildJourney(
  submissions: FormSubmission[],
  definitions: FormDefinition[] = [],
): JourneyStep[] {
  // Skipped on purpose is done for what waits on it (z8nrz7fn5r), as the
  // server's predecessor gate reads it.
  const completed = new Set(
    submissions.filter(s => s.status === 'completed' || s.status === 'skipped').map(s => s.formType),
  )

  const defByType = new Map<CgtFormType, FormDefinition>()
  for (const d of definitions) {
    if (d.formType && !defByType.has(d.formType)) defByType.set(d.formType, d)
  }

  return CGT_FORM_ORDER.map(formType => {
    const submission = submissions.find(s => s.formType === formType)
    const definition = defByType.get(formType)
    const predecessors =
      definition?.predecessorFormTypes ?? CGT_DEFAULT_PREDECESSORS[formType] ?? []
    const blockedBy = predecessors.filter(p => !completed.has(p))

    let status: StepStatus
    if (submission?.status === 'completed') {
      status = 'completed'
    } else if (submission?.status === 'skipped') {
      status = 'skipped'
    } else if (
      submission &&
      (submission.status === 'draft' ||
        submission.status === 'active' ||
        // Filled in and waiting for its reviews (z8nrz7dt41): not done, but
        // certainly started.
        submission.status === 'pending_review')
    ) {
      status = 'in_progress'
    } else if (blockedBy.length > 0) {
      status = 'locked'
    } else {
      status = 'not_started'
    }

    return { formType, label: CGT_FORM_LABELS[formType], status, submission, definition, blockedBy }
  })
}

export interface PatientSummary {
  mrn: string
  steps: JourneyStep[]
  completedCount: number
  total: number
  /** 0 – 1 */
  progress: number
  /** Label of the stage the patient is "at" right now. */
  currentLabel: string
  currentStatus: StepStatus
  /** ISO timestamp of the most recently touched submission. */
  lastUpdated: string | null
}

/** Collapses a patient's submissions into a single overview-row summary. */
export function summarizePatient(
  mrn: string,
  submissions: FormSubmission[],
  definitions: FormDefinition[] = [],
): PatientSummary {
  const steps = buildJourney(submissions, definitions)
  const total = steps.length
  const completedCount = steps.filter(s => s.status === 'completed').length

  const inProgress = steps.find(s => s.status === 'in_progress')
  const nextOpen = steps.find(s => s.status === 'not_started')

  let currentLabel: string
  let currentStatus: StepStatus
  if (completedCount === total) {
    currentLabel = 'Journey complete'
    currentStatus = 'completed'
  } else if (inProgress) {
    currentLabel = inProgress.label
    currentStatus = 'in_progress'
  } else if (nextOpen) {
    currentLabel = `Ready: ${nextOpen.label}`
    currentStatus = 'not_started'
  } else {
    const locked = steps.find(s => s.status === 'locked')
    currentLabel = locked ? `Waiting: ${locked.label}` : 'Not started'
    currentStatus = 'locked'
  }

  const lastUpdated = submissions.reduce<string | null>((latest, s) => {
    return !latest || s.updatedAt > latest ? s.updatedAt : latest
  }, null)

  return {
    mrn,
    steps,
    completedCount,
    total,
    progress: total === 0 ? 0 : completedCount / total,
    currentLabel,
    currentStatus,
    lastUpdated,
  }
}

/** Groups a flat submission list into one summary per patient, newest first. */
export function groupByPatient(
  submissions: FormSubmission[],
  definitions: FormDefinition[] = [],
): PatientSummary[] {
  const byMrn = new Map<string, FormSubmission[]>()
  for (const s of submissions) {
    // No MRN means no patient to summarise. Skipped rather than grouped under a
    // blank key, which would collect every patient-less run into one phantom
    // patient (z8nrz7ckj9).
    if (s.patientMrn === null) continue
    const list = byMrn.get(s.patientMrn) ?? []
    list.push(s)
    byMrn.set(s.patientMrn, list)
  }

  return Array.from(byMrn.entries())
    .map(([mrn, subs]) => summarizePatient(mrn, subs, definitions))
    .sort((a, b) => (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? ''))
}

// ─── Anchored field values for a patient ──────────────────────────────────────

export interface AnchoredValue {
  label: string
  fieldType: CgtFieldType
  value: unknown
}

/** Latest definition version per form name (anchored flags may live on a newer version). */
function latestByName(definitions: FormDefinition[]): Map<string, FormDefinition> {
  const map = new Map<string, FormDefinition>()
  for (const def of definitions) {
    const cur = map.get(def.name)
    if (!cur || def.version > cur.version) map.set(def.name, def)
  }
  return map
}

/**
 * Collects anchored field values across a patient's submissions. Anchored config
 * is read from the latest version of each form (by name); values come from the
 * submission (field/cell IDs are stable across versions). Deduped by label,
 * preferring a filled value.
 */
export function collectAnchoredValues(
  submissions: FormSubmission[],
  definitions: FormDefinition[],
): AnchoredValue[] {
  const latest = latestByName(definitions)
  const defById = new Map(definitions.map(d => [d.id, d]))
  const byLabel = new Map<string, AnchoredValue>()

  // Process newest-first so a later filled value replaces an earlier one
  // deterministically; without this, edits to anchored fields appeared stale.
  const orderedSubmissions = [...submissions].sort((a, b) =>
    (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''),
  )

  const add = (item: AnchoredValue) => {
    const existing = byLabel.get(item.label)
    const filled = (v: unknown) => v !== undefined && v !== null && v !== ''
    if (!existing || (!filled(existing.value) && filled(item.value))) byLabel.set(item.label, item)
  }

  for (const sub of orderedSubmissions) {
    const stepDef = defById.get(sub.formDefinitionId)
    if (!stepDef) continue
    const def = latest.get(stepDef.name) ?? stepDef

    const formIsAnchored = def.category === 'anchored'
    for (const field of allFields(def)) {
      if (field.fieldType === 'table' && field.tableConfig) {
        const tc = field.tableConfig
        for (const row of tc.rows) {
          for (const col of tc.columns) {
            const cell = tc.cells?.[cellKey(row.id, col.id)]
            if (!formIsAnchored && !cell?.anchored) continue
            const resolved = resolveCell(tc, row.id, col)
            // A cell marked not applicable never holds a value (z8nrz7eujb).
            if (resolved.disabled) continue
            add({
              label: `${row.label} · ${col.label}`,
              fieldType: resolved.type === 'calculated' ? 'number' : resolved.type,
              value: sub.values[cellKey(row.id, col.id)],
            })
          }
        }
        continue
      }
      if (!formIsAnchored && !field.anchored) continue
      add({ label: field.label, fieldType: field.fieldType, value: sub.values[field.id] })
    }
  }

  return Array.from(byLabel.values())
}

/**
 * Derives the patient's display name by scanning all submissions for fields
 * labelled "First Name" / "Last Name", regardless of form type or anchored flag.
 */
export function patientNameFromSubmissions(
  submissions: FormSubmission[],
  definitions: FormDefinition[],
): string | null {
  const defById = new Map(definitions.map(d => [d.id, d]))
  for (const sub of submissions) {
    const def = defById.get(sub.formDefinitionId)
    if (!def) continue
    const fields = allFields(def)
    const first = fields.find(f => /first\s*name/i.test(f.label))
    const last = fields.find(f => /last\s*name/i.test(f.label))
    const fv = first ? String(sub.values[first.id] ?? '').trim() : ''
    const lv = last ? String(sub.values[last.id] ?? '').trim() : ''
    const name = [fv, lv].filter(Boolean).join(' ')
    if (name) return name
  }
  return null
}

/**
 * The forms a single-item page references (z8nrz7e8kw): its journey's steps and
 * the submissions it shows, by name. The page fetches every version of just
 * these (formsApi.listDefinitionsByName) instead of the whole library — every
 * lookup it makes is by one of these definitions' ids, or by the same name as
 * one (the "latest version in the same lineage" scan). The submissions list
 * endpoint returns each row's form name with it.
 */
export function referencedFormNames(
  steps: ReadonlyArray<{ formName: string }> | null | undefined,
  submissions: ReadonlyArray<{ formDefinitionId: string; formDefinition?: { name?: string | null } | null }>,
): string[] {
  const names = new Set<string>()
  for (const s of steps ?? []) if (s.formName) names.add(s.formName)
  for (const s of submissions) if (s.formDefinition?.name) names.add(s.formDefinition.name)
  return [...names]
}

/** Best-guess patient display name from anchored fields (First + Last), else null. */
export function patientDisplayName(anchored: AnchoredValue[]): string | null {
  const get = (re: RegExp): string | null => {
    const hit = anchored.find(
      a => re.test(a.label) && typeof a.value === 'string' && a.value.trim() !== '',
    )
    return hit ? String(hit.value).trim() : null
  }
  const first = get(/first\s*name/i)
  const last = get(/last\s*name/i)
  if (first || last) return [first, last].filter(Boolean).join(' ')
  return get(/name/i)
}

// ─── Shared status presentation ───────────────────────────────────────────────

export const STATUS_META: Record<
  StepStatus,
  { label: string; dot: string; text: string; badge: string }
> = {
  completed: {
    label: 'Completed',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  // Done for the run, but nothing was filled: muted, not the green of completed.
  skipped: {
    label: 'Skipped',
    dot: 'bg-slate-400',
    text: 'text-slate-600',
    badge: 'bg-slate-100 text-slate-600 border-slate-300',
  },
  in_progress: {
    label: 'In progress',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  // Yellow, not the amber of in progress: waiting on a reviewer, not on work.
  pending_review: {
    label: 'Pending review',
    dot: 'bg-yellow-400',
    text: 'text-yellow-800',
    badge: 'bg-yellow-50 text-yellow-800 border-yellow-300',
  },
  not_started: {
    label: 'Not started',
    dot: 'bg-slate-300',
    text: 'text-slate-500',
    badge: 'bg-slate-50 text-slate-600 border-slate-200',
  },
  locked: {
    label: 'Locked',
    dot: 'bg-slate-200',
    text: 'text-slate-400',
    badge: 'bg-slate-50 text-slate-400 border-slate-200',
  },
}
